import './styles.css'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import usePermissions from '@/hooks/usePermissions'
import {
  Box,
  Button,
  Center,
  Link as ChakraLink,
  Grid,
  HStack,
  Icon,
  Input,
  InputGroup,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { ModalType, useGlobalModal } from '@infraloop/common'
import { useTranslation } from '@infraloop/i18n'
import {
  CreateInspectionAreaWithFilesParams,
  InspectionArea,
  Project,
  UpdateInspectionAreaWithFilesParams,
} from '@infraloop/services'
import { LuBoxes, LuLayers, LuPencil, LuPlus, LuSearch, LuTrash2 } from 'react-icons/lu'
import { Link as RouterLink, useNavigate } from 'react-router'

import { AREA_GRID_COLUMNS } from '../../layout'
import {
  DEFAULT_SORT,
  SortKey,
  buildAlignmentUrl,
  buildEditorUrl,
  filterAndSortAreas,
  formatDate,
  nextSort,
  parseDashboardHash,
} from '../../utils'
import AreaFormModal from './AreaFormModal'
import AreaSizeCell from './AreaSizeCell'
import RowActionsMenu, { RowAction } from './RowActionsMenu'
import SortableColumnHeader, { ColumnAlign } from './SortableColumnHeader'

/** How long a freshly created area's row stays highlighted (ms). */
const CREATED_AREA_HIGHLIGHT_DURATION = 2500

const InspectionAreaTable = ({
  project,
  areas,
  isLoading,
  isOwned,
  onCreateArea,
  onUpdateArea,
  onDeleteArea,
}: {
  /** Project the areas belong to (editor links, create-modal context and defaults). */
  project: Project
  /** Inspection areas to display. */
  areas: InspectionArea[]
  /** Whether the areas are still loading. */
  isLoading: boolean
  /** Whether this is the user's own project (enables create + modification affordances). */
  isOwned: boolean
  /** Creates an inspection area with its files (the parent row owns the areas query and its mutations). */
  onCreateArea: (params: CreateInspectionAreaWithFilesParams) => Promise<InspectionArea>
  /** Updates an inspection area, its sheet, and replacement files (the parent row owns the mutations). */
  onUpdateArea: (params: UpdateInspectionAreaWithFilesParams) => Promise<InspectionArea>
  /** Deletes an inspection area (the parent row owns the areas query and its mutations). */
  onDeleteArea: (variables: { inspectionAreaId: string }) => Promise<string>
}) => {
  // Hooks
  const { t } = useTranslation()
  const { showModal } = useGlobalModal()
  const { project: projectPermission } = usePermissions()
  const navigate = useNavigate()

  // Derived state — area mutations need both project ownership and write permission.
  const canModify = isOwned && projectPermission.update

  // States
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingArea, setEditingArea] = useState<InspectionArea | null>(null)
  // The area to briefly highlight + reveal. `forceScroll` always centers it (arriving from the
  // editor), otherwise it only scrolls when off-screen (a freshly created area — avoids jumping
  // the page when the new row is already visible).
  const [highlight, setHighlight] = useState<{ areaId: string; forceScroll: boolean } | null>(null)

  // Refs — row elements by area id, for the scroll-to-created-area behaviour.
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({})

  /**
   * Advance the sort state for the clicked column.
   * @param key Column key that was clicked.
   */
  const handleSort = useCallback((key: SortKey) => setSort((s) => nextSort(s, key)), [])

  /**
   * Create the area, then highlight its new row.
   * @param params Area fields and files for the create mutation.
   */
  const handleCreateArea = useCallback(
    async (params: CreateInspectionAreaWithFilesParams) => {
      const created = await onCreateArea(params)
      setHighlight({ areaId: created.inspection_area_id, forceScroll: false })
      return created
    },
    [onCreateArea],
  )

  // Derived state
  const projectId = project.project_id
  const visibleAreas = useMemo(() => filterAndSortAreas(areas, search, sort), [areas, search, sort])

  /**
   * Reveal the highlighted area's row: always centre it when arriving from the editor
   * (`forceScroll`), otherwise only scroll when it isn't already fully visible — so creating
   * from a spot where the row already shows (e.g. the bottom create button on a short list)
   * doesn't jump the page. The highlight clears itself after a moment.
   */
  useEffect(() => {
    if (!highlight) return undefined

    const row = rowRefs.current[highlight.areaId]
    if (row) {
      const rect = row.getBoundingClientRect()
      const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight
      if (highlight.forceScroll || !isFullyVisible) row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const timer = setTimeout(() => setHighlight(null), CREATED_AREA_HIGHLIGHT_DURATION)
    return () => clearTimeout(timer)
  }, [highlight])

  /**
   * On arrival from the editor's back button, the dashboard hash can name the area the user
   * came from (`#<projectId>#<areaId>`). Once this project's areas have loaded, highlight and
   * scroll to that area — reusing the same reveal mechanism as a freshly created area, which is
   * more precise than scrolling to the project when the area list is long. Runs once.
   */
  const didRevealHashArea = useRef(false)
  useEffect(() => {
    if (didRevealHashArea.current) return
    const { projectId: hashProjectId, areaId: hashAreaId } = parseDashboardHash(window.location.hash)
    if (hashProjectId !== project.project_id || !hashAreaId) return
    if (!areas.some((area) => area.inspection_area_id === hashAreaId)) return
    didRevealHashArea.current = true
    setHighlight({ areaId: hashAreaId, forceScroll: true })
  }, [areas, project.project_id])

  /**
   * Build the kebab actions for a single area. Edit opens the area form modal (rename,
   * metadata, replacement mesh); alignment (位置合わせ) navigates to the area's PCD alignment
   * page, the same destination as the editor header's 位置合わせ button. Delete confirms, then
   * runs the mutation — on failure the mutation has toasted the error, so the dialog stays open
   * for a retry.
   * @param area Inspection area the menu belongs to.
   */
  const buildActions = useCallback(
    (area: InspectionArea): RowAction[] => [
      {
        value: 'area-edit',
        label: t('hatsuly.menu.areaEdit'),
        icon: LuPencil,
        onSelect: () => setEditingArea(area),
      },
      {
        value: 'align-area',
        label: t('hatsuly.menu.alignArea'),
        icon: LuLayers,
        onSelect: () => {
          void navigate(buildAlignmentUrl(projectId, area.inspection_area_id))
        },
      },
      {
        value: 'area-delete',
        label: t('hatsuly.menu.areaDelete'),
        icon: LuTrash2,
        danger: true,
        separatorBefore: true,
        onSelect: () =>
          showModal({
            modalType: ModalType.ConfirmationCritical,
            title: t('hatsuly.modal.area.delete.title'),
            body: t('hatsuly.modal.area.delete.body', { name: area.inspection_area_name }),
            confirmText: t('ui.delete'),
            onConfirmPromised: async () => {
              try {
                await onDeleteArea({ inspectionAreaId: area.inspection_area_id })
                return true
              } catch {
                return false
              }
            },
          }),
      },
    ],
    [showModal, t, projectId, navigate, onDeleteArea],
  )

  return (
    <Box
      bg="#FAFBFD"
      borderTop="1px solid"
      borderColor="gray.100"
      boxShadow="inset 3px 0 0 var(--chakra-colors-primary-200)"
      px="18px"
      pb="12px"
      pt="4px"
    >
      {/* toolbar */}
      <HStack justify="space-between" gap="12px" py="12px" px="4px" flexWrap="wrap">
        <Text fontSize="12px" fontWeight="semibold" letterSpacing="0.04em" color="gray.500">
          {t('object.inspectionArea._plural')}
        </Text>
        <HStack gap="10px" flexWrap="wrap">
          <InputGroup w="240px" maxW="60vw" startElement={<LuSearch />}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('hatsuly.area.searchPlaceholder')}
              size="sm"
              borderRadius="8px"
              bg="white"
              data-testid="area-search"
            />
          </InputGroup>
          {canModify && (
            <Button
              variant="outline"
              size="sm"
              gap="6px"
              borderRadius="8px"
              borderColor="gray.300"
              bg="white"
              color="gray.700"
              fontSize="13px"
              fontWeight="semibold"
              _hover={{ bg: 'primary.50', borderColor: 'primary.300', color: 'primary.700' }}
              onClick={() => setIsCreateOpen(true)}
              data-testid="create-area-toolbar"
            >
              <Icon as={LuPlus} boxSize="14px" />
              {t('hatsuly.area.create')}
            </Button>
          )}
        </HStack>
      </HStack>

      {/* header */}
      <Grid
        templateColumns={AREA_GRID_COLUMNS}
        alignItems="center"
        columnGap="8px"
        px="4px"
        py="8px"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Box />
        <SortableColumnHeader
          label={t('object.inspectionArea.name')}
          sortKey={SortKey.Name}
          sort={sort}
          onSort={handleSort}
        />
        <Box />
        <SortableColumnHeader
          label={t('hatsuly.columns.size')}
          sortKey={SortKey.Size}
          sort={sort}
          onSort={handleSort}
          align={ColumnAlign.Right}
          pr="20px"
        />
        <SortableColumnHeader
          label={t('hatsuly.columns.updated')}
          sortKey={SortKey.Updated}
          sort={sort}
          onSort={handleSort}
          align={ColumnAlign.Center}
        />
        <SortableColumnHeader
          label={t('hatsuly.columns.created')}
          sortKey={SortKey.Created}
          sort={sort}
          onSort={handleSort}
          align={ColumnAlign.Center}
        />
        <Box />
      </Grid>

      {/* rows */}
      {isLoading && (
        <Center py="24px">
          <Spinner size="sm" color="primary.500" />
        </Center>
      )}

      {!isLoading &&
        visibleAreas.map((area) => (
          <Grid
            key={area.inspection_area_id}
            ref={(el) => {
              rowRefs.current[area.inspection_area_id] = el
            }}
            templateColumns={AREA_GRID_COLUMNS}
            alignItems="center"
            columnGap="8px"
            px="4px"
            py="12px"
            borderBottom="1px solid"
            borderColor="gray.100"
            borderRadius="8px"
            className={
              highlight?.areaId === area.inspection_area_id && highlight.forceScroll ? 'area-row-pulse' : undefined
            }
            bg={highlight?.areaId === area.inspection_area_id && !highlight.forceScroll ? 'primary.50' : undefined}
            transition="background-color 0.6s"
            _hover={{ bg: '#F8FAFD' }}
          >
            <Center color="primary.400">
              <Icon as={LuBoxes} boxSize="16px" />
            </Center>
            <ChakraLink
              asChild
              display="block"
              minW={0}
              fontSize="14px"
              fontWeight="medium"
              color="primary.700"
              textDecoration="none"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              _hover={{ color: 'primary.600' }}
              data-testid="area-open-editor"
            >
              <RouterLink to={buildEditorUrl(projectId, area.inspection_area_id)}>
                {area.inspection_area_name}
              </RouterLink>
            </ChakraLink>
            <Box />
            <AreaSizeCell area={area} />
            <Text fontSize="13px" color="gray.700" textAlign="center">
              {formatDate(area.updated_at)}
            </Text>
            <Text fontSize="13px" color="gray.400" textAlign="center">
              {formatDate(area.created_at)}
            </Text>
            <Center>{canModify && <RowActionsMenu items={buildActions(area)} size={30} />}</Center>
          </Grid>
        ))}

      {/* Create-area affordance — opens the create modal (files + inspection metadata). */}
      {!isLoading && canModify && (
        <Button
          variant="outline"
          w="100%"
          mt="10px"
          py="11px"
          h="auto"
          gap="8px"
          borderWidth="1.5px"
          borderStyle="dashed"
          borderColor="gray.300"
          borderRadius="9px"
          bg="white"
          color="gray.700"
          fontSize="13px"
          fontWeight="semibold"
          _hover={{ bg: 'primary.50', borderColor: 'primary.300', color: 'primary.700' }}
          onClick={() => setIsCreateOpen(true)}
          data-testid="create-area"
        >
          <Icon as={LuPlus} boxSize="16px" />
          {t('hatsuly.area.create')}
        </Button>
      )}

      {!isLoading && visibleAreas.length === 0 && (
        <Text py="20px" px="4px" color="gray.400" fontSize="13px">
          {t('hatsuly.area.empty')}
        </Text>
      )}

      <AreaFormModal
        project={project}
        areas={areas}
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreateArea}
        onUpdate={onUpdateArea}
      />

      {editingArea && (
        <AreaFormModal
          project={project}
          areas={areas}
          area={editingArea}
          isOpen
          onClose={() => setEditingArea(null)}
          onCreate={onCreateArea}
          onUpdate={onUpdateArea}
        />
      )}
    </Box>
  )
}

export default InspectionAreaTable
